import api from '@/apis'
import Vuetify from '@/plugins/vuetify'

export default {
  state: {
    loading: false,

    // Customizable Questions
    language: 'cantonese',
    questions: [],

    // Extensive Small Talk Toggle
    is_smalltalk: null,

    // Topic Filter
    is_topic_filter: null,
    topic_filter_dialog_visibility: false,
    topic_filter_dialog_loading: false,
    topic_filter_dialog_is_submit_btn_disable: false,
    topic_filter_list: null,

    // Set Prompt
    set_prompt_dialog_visibility: false,
    set_prompt_dialog_loading: false,
    set_prompt_dialog_disable: false,
    set_prompt_dialog_prompt: ''
  },
  mutations: {
    SET_SMALL_TALK_LOADING(state, payload) {
      state.loading = payload
    },
    // Customizable Questions
    SET_SMALL_TALK_LANGUAGE(state, payload) {
      state.language = payload
    },
    SET_SMALL_TALK_QUESTIONS(state, payload) {
      state.questions = payload
    },

    // Extensive Small Talk Toggle
    SET_SMALL_TALK_IS_SMALLTALK(state, payload) {
      state.is_smalltalk = payload
    },

    // Topic Filter
    SET_SMALL_TALK_IS_TOPIC_FILTER(state, payload) {
      state.is_topic_filter = payload
    },
    SET_SMALL_TALK_TOPIC_FILTER_DIALOG_VISIBILITY(state, payload) {
      state.topic_filter_dialog_visibility = payload
    },
    SET_SMALL_TALK_TOPIC_FILTER_DIALOG_LOADING(state, payload) {
      state.topic_filter_dialog_loading = payload
    },
    SET_SMALL_TALK_TOPIC_FILTER_DIALOG_IS_SUBMIT_BTN_DISABLE(state, payload) {
      state.topic_filter_dialog_is_submit_btn_disable = payload
    },
    SET_SMALL_TALK_TOPIC_FILTER_LIST(state, payload) {
      state.topic_filter_list = payload
    },
    ADD_SMALL_TALK_TOPIC_FILTER_GROUP(state, language) {
      state.topic_filter_list[language].push({ topics: [], topics_tag_format: [], speech: null, text: null })
    },
    REMOVE_SMALL_TALK_TOPIC_FILTER_GROUP(state, { language, index }) {
      state.topic_filter_list[language].splice(index, 1)
    },

    // Set Prompt
    SET_SMALL_TALK_SET_PROMPT_DIALOG_VISIBILITY(state, payload) {
      state.set_prompt_dialog_visibility = payload
    },

    SET_SMALL_TALK_SET_PROMPT_DIALOG_DISABLE(state, payload) {
      state.set_prompt_dialog_disable = payload
    },
    SET_SMALL_TALK_SET_PROMPT_DIALOG_LOADING(state, payload) {
      state.set_prompt_dialog_loading = payload
    },
    SET_SMALL_TALK_SET_PROMPT_DIALOG_PROMPT(state, payload) {
      state.set_prompt_dialog_prompt = payload
    }
  },
  actions: {
    // Get smalltalk settings & customizable question list
    async fetchSmallTalkSetting({ state, commit }) {
      try {
        commit('SET_SMALL_TALK_LOADING', true)
        commit('SET_SMALL_TALK_IS_SMALLTALK', null)
        commit('SET_SMALL_TALK_IS_TOPIC_FILTER', null)
        commit('SET_SMALL_TALK_QUESTIONS', [])
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.loading', ''), loading: true })

        const language = state.language
        const [settings, question] = await Promise.all([api.getSmalltalkSetting(), api.getSmallTalkQuestion({ language })])

        if (settings.data.success && question.data.success) {
          commit('SET_SMALL_TALK_LOADING', false)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.success', ''), icon: 'check', timeout: '2000' })

          commit('SET_SMALL_TALK_IS_SMALLTALK', settings.data.data.settings.is_enabled_smalltalk)
          commit('SET_SMALL_TALK_IS_TOPIC_FILTER', settings.data.data.settings.is_enabled_topic_filter)
          commit('SET_SMALL_TALK_QUESTIONS', question.data.data)
        } else if (!settings.data.success) throw Error(settings.data.message)
        else if (!question.data.success) throw Error(question.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    // Update Enable-Status of Topic Filter & Extensive Small Talk
    async updateSmallTalkSetting({ rootState, state, commit }) {
      try {
        const device_id = rootState.auth.selected_device
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })
        const response = await api.updateSmalltalkSetting({ params: { device_id, is_enabled_smalltalk: state.is_smalltalk, is_enabled_topic_filter: state.is_topic_filter } })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.success', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    // Update customizable question list
    async updateSmallTalkQuestion({ commit, rootState }, payload) {
      try {
        const device_id = rootState.auth.selected_device
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })
        const response = await api.updateSmallTalkQuestion({ params: { device_id, ...payload } })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.success', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    async initSmallTalkTopicFilterDialog({ commit }) {
      try {
        commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_VISIBILITY', true)
        commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_LOADING', true)
        commit('SET_SMALL_TALK_TOPIC_FILTER_LIST', null)

        const response = await api.getSmallTalkTopicFilters()
        if (response.data.success) {
          let filters = response.data.data
          Object.keys(filters).forEach((language) => {
            filters[language].map((filter) => {
              filter.topics_tag_format = []
              filter.topics.forEach((topic) => filter.topics_tag_format.push({ text: topic }))
            })
          })
          commit('SET_SMALL_TALK_TOPIC_FILTER_LIST', filters)
          commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_LOADING', false)
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    async updateSmallTalkTopicFilter({ rootState, state, commit }) {
      try {
        const device_id = rootState.auth.selected_device
        commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_IS_SUBMIT_BTN_DISABLE', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })

        Object.keys(state.topic_filter_list).forEach((language) => {
          state.topic_filter_list[language].map((filter) => {
            let temp_topics = []
            filter.topics_tag_format.forEach((tag_format) => temp_topics.push(tag_format.text))
            filter.topics = temp_topics
          })
        })

        const response = await api.updateSmallTalkTopicFilters({ params: { device_id, filters: { ...state.topic_filter_list } } })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.success', ''), icon: 'check', timeout: '2000' })
          commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_VISIBILITY', false)
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_SMALL_TALK_TOPIC_FILTER_DIALOG_IS_SUBMIT_BTN_DISABLE', false)
      }
    },

    // Set Prompt
    async getSmallTalkPrompt({ commit }, payload) {
      try {
        commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_LOADING', true)
        commit('SET_SNACKBAR', {
          status: true,
          color: 'loading',
          text: Vuetify.locale.t('$vuetify.__SnackBar.loading', Vuetify.locale.t('$vuetify.__SmallTalk.prompt.title')),
          loading: true
        })
        const response = await api.getSmallTalkPrompt(payload)
        if (response.data.success) {
          commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_PROMPT', response.data.data.prompt)
          commit('SET_SNACKBAR', {
            status: true,
            color: 'success',
            text: Vuetify.locale.t('$vuetify.__SnackBar.load', Vuetify.locale.t('$vuetify.__SmallTalk.prompt.title')),
            icon: 'check',
            timeout: '2000'
          })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_LOADING', false)
      }
    },
    async setSmallTalkPrompt({ rootState, commit }, payload) {
      try {
        const device_id = rootState.auth.selected_device
        commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_DISABLE', true)
        commit('SET_SNACKBAR', {
          status: true,
          color: 'loading',
          text: Vuetify.locale.t('$vuetify.__SnackBar.updating', Vuetify.locale.t('$vuetify.__SmallTalk.prompt.title')),
          loading: true
        })
        const response = await api.setSmallTalkPrompt({ params: { device_id, ...payload } })
        if (response.data.success) {
          commit('SET_SNACKBAR', {
            status: true,
            color: 'success',
            text: Vuetify.locale.t('$vuetify.__SnackBar.update', Vuetify.locale.t('$vuetify.__SmallTalk.prompt.title')),
            icon: 'check',
            timeout: '2000'
          })
          commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_VISIBILITY', false)
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_SMALL_TALK_SET_PROMPT_DIALOG_DISABLE', false)
      }
    }
  }
}
